import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { Radio, Play, Eye, ExternalLink, Users, AlertTriangle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useLiveStreams, useTopStreamers } from "@/hooks/use-streams";
import { useWallet } from "@/contexts/WalletContext";

export default function Streams() {
  const { connected } = useWallet();
  const { streams, isLoading, error: streamsError, liveCount, totalViewers } = useLiveStreams(20);
  const { data: topStreamers, error: topStreamersError, refetch: refetchTopStreamers } = useTopStreamers("followers", 6);

  // Also get coins with stream URLs as fallback
  const { data: streamingCoins, error: streamingCoinsError, refetch: refetchStreamingCoins } = useQuery({
    queryKey: ["streaming-coins"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coins")
        .select("*")
        .not("stream_url", "is", null)
        .order("market_cap", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  const formatMarketCap = (mc: number | null) => {
    if (!mc) return "$0";
    if (mc >= 1000000) return `$${(mc / 1000000).toFixed(2)}M`;
    if (mc >= 1000) return `$${(mc / 1000).toFixed(1)}K`;
    return `$${mc.toFixed(2)}`;
  };

  const getPlatformIcon = (platform: string | null) => {
    switch (platform?.toLowerCase()) {
      case "youtube": return "🎬";
      case "twitch": return "💜";
      case "tiktok": return "📱";
      case "kick": return "🟢";
      default: return "📺";
    }
  };

  return (
    <>
      <Helmet>
        <title>Live Streams - TheMoonRun</title>
        <meta name="description" content="Watch live streams from coin creators and traders. See who's streaming their trades in real-time." />
      </Helmet>
      
      <PageLayout>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <Radio className="w-7 h-7 text-destructive animate-pulse" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-display font-bold text-foreground">Live Streams</h1>
            <p className="text-muted-foreground">Watch coin creators stream live</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-destructive border-destructive/50">
              <span className="w-2 h-2 rounded-full bg-destructive animate-pulse mr-2" />
              {liveCount} Live
            </Badge>
            <Badge variant="secondary">
              <Eye className="w-3 h-3 mr-1" />
              {totalViewers} Viewers
            </Badge>
          </div>
        </div>

        {/* Become a Streamer CTA */}
        {connected && (
          <div className="p-4 sm:p-6 rounded-xl bg-gradient-to-br from-primary/20 to-accent/10 border border-primary/20 mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-display font-semibold text-foreground mb-1">Start Streaming</h3>
                <p className="text-sm text-muted-foreground">
                  Register as a streamer to go live, earn referral fees, and get 1.25x vote power
                </p>
              </div>
              <Button variant="hero" asChild>
                <Link to="/streamer">
                  <Radio className="w-4 h-4" />
                  Streamer Dashboard
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Top Streamers */}
        {topStreamersError ? (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Top Streamers</h2>
            <div className="flex flex-col items-center justify-center p-8 bg-card border border-border rounded-xl">
              <AlertTriangle className="w-8 h-8 text-destructive mb-2" />
              <p className="text-sm text-muted-foreground mb-2">Failed to load top streamers</p>
              <Button onClick={() => refetchTopStreamers()} variant="outline" size="sm">
                <RefreshCw className="w-3 h-3 mr-1" />
                Retry
              </Button>
            </div>
          </div>
        ) : topStreamers && topStreamers.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Top Streamers</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {topStreamers.map((streamer) => (
                <div
                  key={streamer.id}
                  className="p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-all text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-secondary overflow-hidden mx-auto mb-2">
                    {streamer.profile_image ? (
                      <img src={streamer.profile_image} alt={streamer.display_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg font-bold text-primary">
                        {streamer.display_name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-foreground text-sm truncate">{streamer.display_name}</p>
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Users className="w-3 h-3" />
                    {streamer.follower_count?.toLocaleString() || 0}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live Streams */}
        {streamsError ? (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Live Now</h2>
            <div className="flex flex-col items-center justify-center min-h-[40vh] bg-card border border-border rounded-xl">
              <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Failed to Load Live Streams</h3>
              <p className="text-muted-foreground mb-4 text-center max-w-md">
                {streamsError instanceof Error ? streamsError.message : 'An unexpected error occurred'}
              </p>
              <Button onClick={() => window.location.reload()} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          </div>
        ) : streams.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Live Now</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {streams.map((stream) => (
                <a
                  key={stream.id}
                  href={stream.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group rounded-xl overflow-hidden bg-card border border-border hover:border-primary/50 transition-all"
                  aria-label={`Watch ${stream.title} on ${stream.platform}`}
                >
                  <div className="relative aspect-video bg-secondary">
                    {stream.thumbnail ? (
                      <img src={stream.thumbnail} alt={stream.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Play className="w-10 h-10 text-primary/30 group-hover:text-primary/50 transition-colors" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge className="bg-destructive/90 text-destructive-foreground text-xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse mr-1.5" />
                        LIVE
                      </Badge>
                    </div>
                    <div className="absolute top-3 right-3">
                      <Badge variant="secondary" className="text-xs">
                        {getPlatformIcon(stream.platform)} {stream.platform}
                      </Badge>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <Badge variant="secondary" className="text-xs">
                        <Eye className="w-3 h-3 mr-1" />
                        {stream.viewer_count}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground text-sm truncate mb-1">{stream.title}</h3>
                    {stream.streamers && (
                      <p className="text-xs text-muted-foreground">
                        {stream.streamers.display_name}
                      </p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Coin Streams (fallback) */}
        <h2 className="text-lg font-semibold text-foreground mb-4">Coins with Streams</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {streamingCoinsError ? (
            <div className="col-span-full flex flex-col items-center justify-center min-h-[40vh]">
              <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Failed to Load Streaming Coins</h3>
              <p className="text-muted-foreground mb-4 text-center max-w-md">
                {streamingCoinsError instanceof Error ? streamingCoinsError.message : 'An unexpected error occurred'}
              </p>
              <Button onClick={() => refetchStreamingCoins()} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl overflow-hidden bg-card border border-border">
                <Skeleton className="aspect-video" />
                <div className="p-4">
                  <Skeleton className="h-5 w-24 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            ))
          ) : streamingCoins && streamingCoins.length > 0 ? (
            streamingCoins.map((coin) => (
              <div
                key={coin.id}
                className="group rounded-xl overflow-hidden bg-card border border-border hover:border-primary/50 transition-all"
              >
                <div className="relative aspect-video bg-secondary">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Play className="w-10 h-10 text-primary/30 group-hover:text-primary/50 transition-colors" />
                  </div>
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge className="bg-destructive/90 text-destructive-foreground text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse mr-1.5" />
                      LIVE
                    </Badge>
                  </div>
                  <div className="absolute top-3 right-3">
                    <Badge variant="secondary" className="text-xs">
                      {getPlatformIcon(coin.stream_platform)}
                    </Badge>
                  </div>
                  <a
                    href={coin.stream_url ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0"
                    aria-label={`Watch ${coin.name} on ${coin.stream_platform}`}
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-secondary overflow-hidden">
                        {coin.image ? (
                          <img src={coin.image} alt={coin.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-sm font-bold text-primary">
                            {coin.ticker.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">{coin.name}</h3>
                        <p className="text-xs text-muted-foreground">${coin.ticker}</p>
                      </div>
                    </div>
                    <a
                      href={coin.stream_url ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
                      aria-label={`Open stream for ${coin.name} in new tab`}
                    >
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </a>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Market Cap</span>
                    <span className="font-mono text-primary">{formatMarketCap(coin.market_cap)}</span>
                  </div>
                  <Link
                    to={`/coin/${coin.id}`}
                    className="block mt-3 text-center text-xs text-primary hover:underline"
                  >
                    View Coin →
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full p-12 text-center">
              <Radio className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No live streams right now</p>
              <p className="text-sm text-muted-foreground">Check back later or create a coin with streaming!</p>
            </div>
          )}
        </div>
      </PageLayout>
    </>
  );
}
